import { AUDIENCE_OVERLAP_PROVIDERS } from "src/app/components/AudienceOverlap/constants";
import { AudienceOverlapSocialProfile } from "src/app/components/AudienceOverlap/creatorsAudienceOverlapApi";

export {
  AUDIENCE_OVERLAP_PROVIDERS,
  AUDIENCE_OVERLAP_POLLING_INTERVAL,
  AUDIENCE_OVERLAP_POLLING_MAX_ATTEMPTS,
  AUDIENCE_OVERLAP_POLLING_MAX_MISSING_STATES,
} from "src/app/components/AudienceOverlap/constants";
export type { AudienceOverlapSocialProfile };

// The overlap section has its own grid: 20 rows of 10px with 10px gaps fill one page
export const AUDIENCE_OVERLAP_GRID = {
  cols: 12,
  rows: 20,
  rowHeight: 10,
  margin: [24, 10] as [number, number],
};

const HEADER_HEIGHT = 3;
const SUMMARY_HEIGHT = 13;
const PLATFORM_ONLY_HEIGHT = 4;
const CREATOR_HEIGHT = 4;

export type AudienceOverlapBoxType =
  | "header"
  | "platform"
  | "chart"
  | "creator"
  | "warning";

export interface AudienceOverlapBox {
  id: string;
  type: AudienceOverlapBoxType;
  provider: string;
  reportInfo?: { totalFollowers: number; totalUniqueFollowers: number };
  creator?: {
    name: string;
    cover?: string | null;
    followers?: number;
    overlappingPercentage?: number;
    uniquePercentage?: number;
    errorMessage?: string;
  };
}

export interface AudienceOverlapLayoutItem {
  i: string;
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface AudienceOverlapPage {
  id: string;
  provider: string;
  layout: AudienceOverlapLayoutItem[];
}

interface ShowcaseElement {
  id: string;
  name: string;
  cover?: string | null;
  socialProfiles?: { socialProvider: string; socialUsername: string }[];
}

export const normalizeProvider = (provider: string) =>
  provider === "tik_tok" ? "tiktok" : provider;

/**
 * Social profiles of the creators in the showcase, limited to the platforms audience overlap is available on.
 */
export const getAudienceOverlapSocialProfiles = (
  elements: ShowcaseElement[] = [],
): AudienceOverlapSocialProfile[] => {
  const socialProfiles: AudienceOverlapSocialProfile[] = [];

  elements.forEach((element) => {
    (element.socialProfiles || []).forEach((profile) => {
      const socialProvider = normalizeProvider(profile.socialProvider);

      if (
        !profile.socialUsername ||
        !AUDIENCE_OVERLAP_PROVIDERS.includes(socialProvider)
      )
        return;

      socialProfiles.push({
        socialProvider,
        socialUsername: profile.socialUsername,
      });
    });
  });

  return socialProfiles;
};

/**
 * Audience overlap compares creators within one platform, so at least two accounts on the same one are needed.
 */
export const hasEnoughSocialProfilesForAudienceOverlap = (
  socialProfiles: AudienceOverlapSocialProfile[],
) =>
  AUDIENCE_OVERLAP_PROVIDERS.some((provider) => {
    const usernames = socialProfiles
      .filter((profile) => profile.socialProvider === provider)
      .map((profile) => profile.socialUsername.trim().toLowerCase());

    return new Set(usernames).size >= 2;
  });

export const findElement = (
  elements: ShowcaseElement[],
  provider: string,
  username: string,
) =>
  elements.find((element) =>
    element.socialProfiles?.some(
      (profile) =>
        normalizeProvider(profile.socialProvider) === provider &&
        profile.socialUsername?.toLowerCase() === username?.toLowerCase(),
    ),
  );

const getCreatorBoxes = (
  provider: string,
  audienceOverlap: any,
  elements: ShowcaseElement[],
): AudienceOverlapBox[] => {
  const creators = [...(audienceOverlap?.data || [])].sort((a, b) =>
    String(a.username).localeCompare(String(b.username)),
  );

  const boxes: AudienceOverlapBox[] = creators.map((creator) => {
    const element = findElement(elements, provider, creator.username);

    return {
      id: `overlap-creator-${provider}-${creator.username}`,
      type: "creator",
      provider,
      creator: {
        name: element?.name || creator.username,
        cover: element?.cover,
        followers: creator.followers,
        overlappingPercentage: creator.overlappingPercentage,
        uniquePercentage: creator.uniquePercentage,
      },
    };
  });

  const errors = audienceOverlap?.errorsWithCalculatingOverlap || {};

  // an empty list of errors comes from the API as an array
  if (Array.isArray(errors)) return boxes;

  Object.entries(errors).forEach(([username, errorMessage]) => {
    const element = findElement(elements, provider, username);

    boxes.push({
      id: `overlap-warning-${provider}-${username}`,
      type: "warning",
      provider,
      creator: {
        name: element?.name || username,
        cover: element?.cover,
        errorMessage: String(errorMessage),
      },
    });
  });

  return boxes;
};

/**
 * Turns the analysis result into pages of boxes. Every platform starts on a new page with its summary,
 * creators follow on the next pages. A platform without any result gets only the warnings.
 */
export const buildAudienceOverlapPages = (
  audienceOverlapData: { [provider: string]: any },
  elements: ShowcaseElement[] = [],
) => {
  const pages: AudienceOverlapPage[] = [];
  const boxes: { [id: string]: AudienceOverlapBox } = {};

  const addBox = (box: AudienceOverlapBox) => {
    boxes[box.id] = box;

    return box.id;
  };

  AUDIENCE_OVERLAP_PROVIDERS.forEach((provider) => {
    const audienceOverlap = audienceOverlapData?.[provider]?.audienceOverlap;

    if (!audienceOverlap) return;

    const hasSummary = Boolean(audienceOverlap.data?.length);
    const creatorBoxes = getCreatorBoxes(provider, audienceOverlap, elements);

    if (!hasSummary && !creatorBoxes.length) return;

    const { cols, rows } = AUDIENCE_OVERLAP_GRID;
    const firstPage: AudienceOverlapPage = {
      id: `overlap-page-${provider}-0`,
      provider,
      layout: [
        {
          i: addBox({
            id: `overlap-header-${provider}`,
            type: "header",
            provider,
          }),
          x: 0,
          y: 0,
          w: cols,
          h: HEADER_HEIGHT,
        },
      ],
    };

    pages.push(firstPage);

    if (hasSummary) {
      firstPage.layout.push(
        {
          i: addBox({
            id: `overlap-platform-${provider}`,
            type: "platform",
            provider,
          }),
          x: 0,
          y: HEADER_HEIGHT,
          w: cols / 3,
          h: SUMMARY_HEIGHT,
        },
        {
          i: addBox({
            id: `overlap-chart-${provider}`,
            type: "chart",
            provider,
            reportInfo: audienceOverlap.reportInfo,
          }),
          x: cols / 3,
          y: HEADER_HEIGHT,
          w: (cols / 3) * 2,
          h: SUMMARY_HEIGHT,
        },
      );
    } else {
      firstPage.layout.push({
        i: addBox({
          id: `overlap-platform-${provider}`,
          type: "platform",
          provider,
        }),
        x: 0,
        y: HEADER_HEIGHT,
        w: cols,
        h: PLATFORM_ONLY_HEIGHT,
      });
    }

    // warnings of a platform without summary start right below its header
    let currentPage = hasSummary ? null : firstPage;
    let currentRow = hasSummary ? 0 : HEADER_HEIGHT + PLATFORM_ONLY_HEIGHT;

    creatorBoxes.forEach((box) => {
      if (!currentPage || currentRow + CREATOR_HEIGHT > rows) {
        currentPage = {
          id: `overlap-page-${provider}-${pages.length}`,
          provider,
          layout: [],
        };
        currentRow = 0;
        pages.push(currentPage);
      }

      currentPage.layout.push({
        i: addBox(box),
        x: 0,
        y: currentRow,
        w: cols,
        h: CREATOR_HEIGHT,
      });
      currentRow += CREATOR_HEIGHT;
    });
  });

  return { pages, boxes };
};
